import { Suspense, useEffect } from 'react'
import { Outlet } from 'react-router-dom'

import { DesktopSiteNotice } from '@/components/layout/desktop-site-notice'
import { RouteFallback } from '@/components/layout/route-fallback'
import { Sidebar } from '@/components/layout/sidebar'
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar'
import { useUserProfile } from '@/app/providers/user-profile-provider'
import { useDataMode } from '@/app/providers/data-mode-provider'
import { useSubscriptionContext } from '@/app/providers/subscription-provider'
import { SupportRouteObserver } from '@/app/providers/support-route-observer'
import { Badge } from '@/components/ui/badge'
import { BillingBanner } from '@/components/billing/billing-banner'
import { NotificationToaster } from '@/components/notifications/notification-toaster'
import { cn } from '@/lib/utils'

export function AppShell() {
  const { mode } = useDataMode()
  // Sıkı kenar çubuğu + otomatik ikon rayı yalnız yöneticiler için (yönetici değilse eski davranış).
  const { profile } = useUserProfile()
  // #920 / S143: koyu temada okunur gri jetonlar önce yalnız yöneticilerde (styles/global.css, en altta).
  useEffect(() => {
    const root = document.documentElement
    if (profile.isAdmin) root.setAttribute('data-contrast-admin', '')
    else root.removeAttribute('data-contrast-admin')
    return () => root.removeAttribute('data-contrast-admin')
  }, [profile.isAdmin])
  const {
    isPaused,
    isGracePeriod,
    isGiftExpired,
    isTrialExpired,
    isCancelPending,
    trialDaysRemaining,
  } = useSubscriptionContext()

  const showDemoBanner = mode === 'demo'
  const hasBillingBanner =
    isPaused ||
    isGracePeriod ||
    isGiftExpired ||
    isTrialExpired ||
    isCancelPending ||
    (trialDaysRemaining !== null && trialDaysRemaining <= 3)
  const hasBanner = showDemoBanner || hasBillingBanner
  const insetPaddingClass = hasBanner
    ? 'px-6 pb-10 pt-2 lg:px-10'
    : 'px-6 pb-10 pt-6 lg:px-10'

  return (
    <SidebarProvider className="w-full" compact={profile.isAdmin}>
      <SupportRouteObserver />
      {/* N2-03: authenticated layout içinde always-on toast + uçan overlay */}
      <NotificationToaster />
      <div className="flex min-h-svh w-full bg-gradient-to-br from-background via-background/98 to-muted/40 transition-[width] duration-300 ease-in-out">
        <Sidebar />
        {/* Listing Builder çalışma alanı (data-lb-full-bleed) varken: 230,4 px kenar çubuğu + 900 px = 1130,4 px;
            yalnız çalışma alanı iki sütun olabilecek genişlikte sayfa yüksekliği sabitlenir, daha dar görünümde sayfa normal kayar. */}
        <SidebarInset
          className={cn(
            'flex flex-1 flex-col min-w-0 bg-gradient-to-br from-background via-background/98 to-muted/40',
            'has-[[data-lb-full-bleed]]:min-[1131px]:h-svh',
            'has-[[data-lb-full-bleed]]:min-[1131px]:overflow-hidden',
            // Kai sohbeti (data-kai-chat): her genişlikte yükseklik sabit; mesaj listesi kendi içinde kayar, yazı kutusu görünür kalır (#1126)
            'has-[[data-kai-chat]]:h-svh',
            'has-[[data-kai-chat]]:overflow-hidden',
          )}
        >
          {/* #1154: telefonda "bilgisayardan girin" şeridi (erişim anahtarı kapalıysa hiçbir şey çizilmez) */}
          <DesktopSiteNotice />
          <div className="sticky top-0 z-30 flex h-14 shrink-0 items-center border-b border-border/60 bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/80 md:hidden">
            <SidebarTrigger
              className="h-9 w-9 rounded-lg border border-border bg-card text-foreground shadow-sm"
              aria-label="Menüyü aç"
              title="Menüyü aç"
            />
          </div>
          <BillingBanner />
          {showDemoBanner && (
            <div className="flex flex-col gap-4 px-6 pt-4 lg:px-10">
              <div className="flex flex-col gap-1 rounded-xl border border-brand-purple/25 bg-brand-purple/10 px-4 py-3 text-sm text-brand-purple shadow-sm">
                <Badge
                  variant="secondary"
                  className="w-max border-none bg-brand-purple text-xs font-semibold uppercase tracking-wide text-white"
                >
                  Demo Modu
                </Badge>
                <p className="text-brand-purple/80">
                  Gösterilen veriler örnek amaçlıdır; backend ile iletişim kurulmaz.
                </p>
              </div>
            </div>
          )}
          {/* Tam-kenar çalışma alanında dolgu iptal edilir ve açılışta animasyonlu sıçrama olmaz (D-01, PREV-06) */}
          <div
            className={cn(
              'flex flex-1 flex-col min-w-0 transition-[padding,width] duration-300 ease-in-out',
              insetPaddingClass,
              'has-[[data-lb-full-bleed]]:!p-0',
              'has-[[data-lb-full-bleed]]:min-h-0',
              'has-[[data-lb-full-bleed]]:transition-none',
              'has-[[data-kai-chat]]:min-h-0',
            )}
          >
            {/* #1090: sayfa kodu tembel iner; kenar çubuğu ve üst bölüm yerinde kalır, yalnız içerik alanı bekler */}
            <Suspense fallback={<RouteFallback />}>
              <Outlet />
            </Suspense>
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  )
}
